import { Column } from 'primereact/column';
import { DataTableRowMouseEventParams } from 'primereact/datatable';
import React, { useCallback, useRef } from 'react';
import Table from '../../../../components/Table/Table';
import ErrorToast from '../../../../components/messages/ErrorAlert';
import { Toast } from 'primereact/toast';
import { animateCellText } from '@/src/utils/htmlUtils';
import UM from './cells/UM';
import PhaseCode from './cells/PhaseCode';
import { formatCurrency } from '@/src/utils/formatUtils';
import { SubcontractChangeOrderResult } from '../../../../Interfaces/Accounting/ACForms.interfaces';
import { Tooltip } from 'primereact/tooltip';

type SLChangeOrderSuccessTableProps = {
  items: (SubcontractChangeOrderResult & { isNew: boolean })['newItems'];
  job: string;
};

const SLChangeOrderSuccessTable = ({
  items,
  job,
}: SLChangeOrderSuccessTableProps) => {
  const toast = useRef<Toast>(null);

  const onRowHover = (e: DataTableRowMouseEventParams) => {
    const cell = e.originalEvent.target as HTMLElement;
    const row = cell.closest('tr') as HTMLElement;
    animateCellText(row);
  };

  let totalCosts = 0;
  items?.forEach((item) => {
    totalCosts += item.total || 0;
  });

  return (
    <React.Fragment>
      <ErrorToast toastRef={toast} />
      <Table
        id="sl-co-success-items"
        data={items ?? []}
        dataKey="item"
        className={`mx-3 dashboardOptionShadow with-expanded noPadding noHover`}
        onRowMouseEnter={useCallback(onRowHover, [])}
        fixedDefaultHeight={790}
        hideColumns={false}
      >
        <Column
          body={(item) => {
            if (item.isNew) {
              return (
                <React.Fragment>
                  <i
                    id={`new-item-${item.item}`}
                    className="pi pi-plus-circle text-standard text-green-500 newItemRow printHide"
                    data-pr-position="left"
                    data-pr-tooltip="New Item"
                  />
                  <Tooltip target={`#new-item-${item.item}`} />
                  <span className="printMedia printShow">New Item</span>
                </React.Fragment>
              );
            } else {
              return (
                <React.Fragment>
                  <i
                    id={`edition-item-${item.item}`}
                    className="pi pi-pencil text-standard text-bluwaiBlue printHide"
                    data-pr-position="left"
                    data-pr-tooltip="Edited Item"
                  />
                  <Tooltip target={`#edition-item-${item.item}`} />
                  <span className="printMedia printShow">Edited Item</span>
                </React.Fragment>
              );
            }
          }}
          style={{ minWidth: '70px', maxWidth: '70px' }}
          headerClassName={`tableHeader font-normal`}
          className={`text-standard blackText tableCell justify-content-center`}
          footerClassName="tableFooter"
        />
        <Column
          field="item"
          header="Item"
          style={{ minWidth: '100px', maxWidth: '100px' }}
          headerClassName="tableHeader font-normal text-center"
          className="justify-content-center text-standard blackText tableCell"
          footerClassName="tableFooter"
          sortable
        />
        <Column
          field="description"
          header="Description"
          body={(item) => {
            return <div className="scroll-text">{item.description}</div>;
          }}
          style={{ minWidth: '300px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className=" text-standard blackText tableCell overflow-x-hidden white-space-nowrap checkOverflow"
          footerClassName="tableFooter"
        />
        <Column
          field="phase"
          header="Phase Code"
          body={(item) => {
            return (
              <div className="scroll-text">
                <PhaseCode
                  item={item}
                  job={job}
                  fetchData={false}
                  textMode={true}
                />
              </div>
            );
          }}
          style={{ minWidth: '300px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className="text-standard blackText tableCell overflow-x-hidden white-space-nowrap checkOverflow"
          footerClassName="tableFooter"
        />
        <Column
          field="um"
          header="UM"
          body={(item) => {
            return (
              <div className="scroll-text">
                <UM item={item} fetchData={false} textMode={true} />
              </div>
            );
          }}
          style={{ minWidth: '150px', maxWidth: '150px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className="text-standard blackText tableCell overflow-x-hidden white-space-nowrap checkOverflow"
          footerClassName="tableFooter"
        />
        <Column
          field="units"
          header="Current Units"
          body={(item) => {
            return item.units;
          }}
          style={{ minWidth: '150px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className="text-standard blackText tableCell justify-content-center"
          footerClassName="tableFooter"
        />
        <Column
          field="unitCosts"
          header="Current Unit Costs"
          body={(item) => {
            return formatCurrency(item.unitCosts);
          }}
          style={{ minWidth: '150px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className="justify-content-center text-standard blackText tableCell"
          footer="Total"
          footerClassName="tableFooter block text-right border-top-2 border-transparent mt-4 limitBorder relative"
        />
        <Column
          field="total"
          header="Current Costs"
          body={(item) => {
            return formatCurrency(item.total);
          }}
          style={{ minWidth: '150px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className="justify-content-end text-standard blackText tableCell"
          footer={formatCurrency(totalCosts)}
          footerClassName="tableFooter block text-right border-top-2 mt-4"
        />
        <Column
          field="retainage"
          header="Retainage"
          body={(item) => {
            return `${item.retainage * 100}%`;
          }}
          style={{ minWidth: '150px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className="justify-content-center text-standard blackText tableCell"
          footerClassName="tableFooter"
        />
      </Table>
    </React.Fragment>
  );
};

export default SLChangeOrderSuccessTable;
